/// <reference types="googlemaps" />
import React, { FC, useRef } from "react";
import { searchClubs, socialFind, getPlaceDetails } from "../actions";
import { PlacePrediction, ClubAutocompleteResult, MultiSearchResult, User } from "../types";
import AutocompleteInput from "./autocompleteInput";
import { formatUserName } from "../libs/formatName";
import PlayerItemMedium from "./items/player/medium";
import CityItemMedium from "./items/city/medium";
import ClubItemMedium from "./items/club/medium";

const findCountry = (result: google.maps.GeocoderResult): string => {
	for (let component of result.address_components) {
		if (component.types.includes("country")) {
			return component.short_name;
		}
	}

	return undefined;
};

const renderSuggestion = (suggestion: MultiSearchResult) => {
	if (suggestion.type === "place") {
		const place = suggestion.place;
		return (
			<CityItemMedium
				name={place.structured_formatting.main_text}
				meta={place.structured_formatting.secondary_text}
				extra="search"
			/>
		);
	}

	if (suggestion.type === "club") {
		const club = suggestion.club;
		return <ClubItemMedium name={club.name} meta={club.city} photoUrl={club.photoUrl} extra="search" />;
	}

	if (suggestion.type === "player") {
		const player = suggestion.player;
		return (
			<PlayerItemMedium
				name={formatUserName(player)}
				meta={player.city}
				photoUrl={player.photoUrl}
				className="search"
			/>
		);
	}
};

const getDisplayValueDefaultFunction = (value: any) => value;

type OnChange = (suggestion: MultiSearchResult | string) => void;

type AllowedTypes = MultiSearchResult["type"][];

type Props = {
	valueProperty?: string;
	value?: string;
	onChange: OnChange;
	placeholder?: string;
	getDisplayValue?: (value: any) => string;
	types: AllowedTypes;
	enhancePlaceWithCountry?: boolean;
};

function combineResults(
	predictions: PlacePrediction[],
	clubs: ClubAutocompleteResult[],
	players: User[]
): MultiSearchResult[] {
	let results = [] as MultiSearchResult[];

	const place = predictions[0];

	if (place) {
		results[0] = {
			type: "place",
			place: place,
			value: place.structured_formatting.main_text,
			description: place.description,
		};
	}

	for (let club of clubs) {
		results.push({
			type: "club",
			club: club,
			value: club.name,
			description: club.name,
		});
	}

	for (let player of players) {
		const playerName = formatUserName(player);
		results.push({
			type: "player",
			player: player,
			value: playerName,
			description: playerName,
		});
	}

	return results;
}

function typesInclude(types: AllowedTypes, type: MultiSearchResult["type"]): boolean {
	return types.includes(type);
}

const MultiSearchInput: FC<Props> = ({
	value,
	valueProperty = "value",
	onChange,
	placeholder,
	getDisplayValue = getDisplayValueDefaultFunction,
	types,
	enhancePlaceWithCountry = false,
}: Props) => {
	const autocompleteService = new google.maps.places.AutocompleteService();
	const geocoder = new google.maps.Geocoder();

	const input = useRef<HTMLInputElement>();

	const getAutocomplete = (input: string): Promise<PlacePrediction[]> => {
		return new Promise((resolve, reject) => {
			autocompleteService.getPlacePredictions(
				{
					input,
					types: ["(cities)"],
				},
				(results: PlacePrediction[], status: google.maps.places.PlacesServiceStatus) => {
					if (results) {
						resolve(results);
					} else {
						resolve([]);
						//reject(new Error("no places"));
					}
				}
			);
		});
	};

	const handleChange = async (value: string): Promise<MultiSearchResult[]> => {
		onChange(value);

		let places = [] as PlacePrediction[];
		let clubs = [] as ClubAutocompleteResult[];
		let players = [] as User[];

		if (typesInclude(types, "place")) {
			places = await getAutocomplete(value);
		}

		if (typesInclude(types, "club")) {
			clubs = await searchClubs(value);
		}

		if (typesInclude(types, "player")) {
			players = (await socialFind({ term: value })).data;
		}

		return combineResults(places, clubs, players);
	};

	const onItemSelected = async (suggestion: MultiSearchResult) => {
		if (suggestion.type === "place") {
			const { latitude, longitude, countryCode } = await getPlaceDetails(suggestion.place.place_id);
			suggestion.location = {
				lat: latitude,
				lng: longitude,
			};
			suggestion.country = countryCode;
		}

		onChange(suggestion);
	};

	return (
		<AutocompleteInput
			value={getDisplayValue(value)}
			placeholder={placeholder}
			onChange={handleChange}
			getSuggestionValue={(suggestion) => suggestion[valueProperty]}
			renderSuggestion={renderSuggestion}
			onItemSelected={onItemSelected}
			ref={input}
		/>
	);
};

export default MultiSearchInput;
